import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  TbBuildingStore, TbCalendarCheck, TbCash, TbUsers,
  TbPercentage, TbCurrencyEuro, TbArrowRight, TbRefresh,
  TbSunrise, TbHome, TbMoonStars, TbAlertTriangle,
  TbCircleCheck, TbHourglass
} from "react-icons/tb";

const API = "https://api.komorotel.com/api/agency";
const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR");
const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const fmtTime = () => new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

function KpiCard({ icon: Icon, label, value, sub, subColor, color = "gray", badge }) {
  const colors = {
    green:  { bg: "bg-emerald-50",  icon: "text-emerald-600", val: "text-emerald-700" },
    blue:   { bg: "bg-blue-50",     icon: "text-blue-600",    val: "text-blue-700" },
    orange: { bg: "bg-orange-50",   icon: "text-orange-500",  val: "text-orange-600" },
    purple: { bg: "bg-violet-50",   icon: "text-violet-600",  val: "text-violet-700" },
    gray:   { bg: "bg-gray-50",     icon: "text-gray-500",    val: "text-gray-800" },
  };
  const c = colors[color];
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center`}>
          <Icon className={`${c.icon} text-lg`} />
        </div>
        {badge && badge}
      </div>
      <div>
        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
        <p className={`text-2xl font-bold tracking-tight ${c.val}`}>{value}</p>
        {sub && <p className={`text-xs mt-0.5 ${subColor || "text-gray-400"}`}>{sub}</p>}
      </div>
    </div>
  );
}

function AlertCard({ icon: Icon, title, desc, color, onClick }) {
  const colors = {
    orange: { border: "border-orange-200", bg: "bg-orange-50", icon: "text-orange-500", title: "text-orange-800", desc: "text-orange-600" },
    blue:   { border: "border-blue-200",   bg: "bg-blue-50",   icon: "text-blue-500",   title: "text-blue-800",   desc: "text-blue-600" },
  };
  const c = colors[color] || colors.orange;
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3.5 rounded-xl border ${c.border} ${c.bg} cursor-pointer hover:opacity-90 transition`}
    >
      <Icon className={`${c.icon} shrink-0`} size={18} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold ${c.title}`}>{title}</p>
        <p className={`text-xs ${c.desc} truncate`}>{desc}</p>
      </div>
      <TbArrowRight className={`${c.icon} shrink-0`} size={14} />
    </div>
  );
}

export default function AgencyDashboard({ setActivePage }) {
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [time, setTime]             = useState(fmtTime());

  useEffect(() => {
    const t = setInterval(() => setTime(fmtTime()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [lastRefresh]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";
  const GreetIcon = hour < 12 ? TbSunrise : hour < 18 ? TbHome : TbMoonStars;

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GreetIcon className="text-[#3A9D23]" size={18} />
            <p className="text-sm text-gray-500 font-medium capitalize">{greeting} — {fmtDate(new Date())}</p>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Dashboard agence</h1>
          <p className="text-sm text-gray-400 mt-0.5">Vue d'ensemble de votre portefeuille</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white border border-gray-100 rounded-2xl px-4 py-2.5 shadow-sm text-center">
            <p className="text-xs text-gray-400">Heure locale</p>
            <p className="text-lg font-bold text-gray-900 font-mono">{time}</p>
          </div>
          <button
            onClick={() => setLastRefresh(new Date())}
            className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm hover:bg-gray-50 transition text-gray-400 hover:text-gray-600"
          >
            <TbRefresh size={18} />
          </button>
        </div>
      </div>

      {/* ── ACCÈS RAPIDES ── */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Accès rapides</p>
        <div className="flex gap-3">
          {[
            { label: "Mes biens",      sub: "Gérer le portefeuille", icon: TbBuildingStore, page: "hotels",       color: "text-[#3A9D23]", bg: "bg-green-50"  },
            { label: "Réservations",   sub: "Toutes les résa",       icon: TbCalendarCheck, page: "reservations", color: "text-blue-600",  bg: "bg-blue-50"   },
            { label: "Propriétaires",  sub: "Gérer les owners",      icon: TbUsers,         page: "owners",       color: "text-violet-600",bg: "bg-violet-50" },
            { label: "Reversements",   sub: "Suivi des payouts",     icon: TbCash,          page: "payouts",      color: "text-amber-600", bg: "bg-amber-50"  },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={() => setActivePage?.(item.page)}
                className="flex-1 flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-200 transition-all text-left group"
              >
                <div className={`w-9 h-9 rounded-xl ${item.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={item.color} size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900 truncate">{item.label}</p>
                  <p className="text-xs text-gray-400 truncate">{item.sub}</p>
                </div>
                <TbArrowRight className="text-gray-300 group-hover:text-gray-500 transition shrink-0" size={13} />
              </button>
            );
          })}
        </div>
      </div>

      {/* ── ALERTES ── */}
      {stats?.payouts?.pendingCount > 0 && (
        <div className="flex flex-col gap-2">
          <AlertCard
            icon={TbAlertTriangle}
            title={`${stats.payouts.pendingCount} reversement${stats.payouts.pendingCount > 1 ? "s" : ""} en attente`}
            desc={`${stats.payouts.totalPending?.toLocaleString("fr-FR")} € à verser aux propriétaires`}
            color="orange"
            onClick={() => setActivePage?.("payouts")}
          />
        </div>
      )}

      {/* ── KPI CARDS ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={TbBuildingStore}
          label="Biens actifs"
          value={loading ? "…" : stats?.hotels?.active ?? 0}
          sub={stats ? `${stats.hotels.inactive} inactif${stats.hotels.inactive > 1 ? "s" : ""}` : ""}
          color="green"
        />
        <KpiCard
          icon={TbCalendarCheck}
          label="Réservations ce mois"
          value={loading ? "…" : stats?.reservations?.thisMonth ?? 0}
          sub={`${stats?.reservations?.total ?? 0} au total`}
          color="blue"
        />
        <KpiCard
          icon={TbPercentage}
          label="Taux d'occupation"
          value={loading ? "…" : `${stats?.occupancyRate ?? 0}%`}
          sub="30 derniers jours"
          color="purple"
        />
        <KpiCard
          icon={TbCurrencyEuro}
          label="CA ce mois"
          value={loading ? "…" : `${stats?.reservations?.revenueMonth?.toLocaleString("fr-FR") ?? 0} €`}
          sub={stats ? `${toKMF(stats.reservations.revenueMonth)} KMF` : ""}
          color="green"
          badge={
            stats?.payouts?.totalPending > 0 ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-500 font-semibold">
                {stats.payouts.totalPending.toLocaleString("fr-FR")} € à reverser
              </span>
            ) : null
          }
        />
      </div>

      {/* ── GRILLE PRINCIPALE ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Colonne gauche */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Finances */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                <TbCurrencyEuro className="text-[#3A9D23]" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Finances du portefeuille</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "CA total toutes périodes", val: stats?.reservations?.revenueMonth },
                { label: "Reversements en attente",  val: stats?.payouts?.totalPending, color: "text-orange-500" },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                  <p className={`text-lg font-bold ${item.color || "text-gray-900"}`}>
                    {item.val != null ? `${item.val.toLocaleString("fr-FR")} €` : "…"}
                  </p>
                  {item.val != null && (
                    <p className="text-xs text-gray-400 mt-0.5">{toKMF(item.val)} KMF</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Biens */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                  <TbBuildingStore className="text-[#3A9D23]" size={14} />
                </div>
                <p className="text-sm font-semibold text-gray-900">Portefeuille</p>
              </div>
              <button
                onClick={() => setActivePage?.("hotels")}
                className="text-xs text-[#3A9D23] hover:underline flex items-center gap-1"
              >
                Voir tout <TbArrowRight size={12} />
              </button>
            </div>
            <div className="flex flex-col gap-0">
              {[
                { label: "Biens total",   value: stats?.hotels?.total   ?? "…", dot: "bg-gray-300" },
                { label: "Actifs",        value: stats?.hotels?.active   ?? "…", dot: "bg-emerald-400" },
                { label: "Inactifs",      value: stats?.hotels?.inactive ?? "…", dot: "bg-orange-300" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${item.dot}`} />
                    <p className="text-xs text-gray-600">{item.label}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Colonne droite */}
        <div className="flex flex-col gap-4">

          {/* Reversements */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
                <TbCash className="text-amber-600" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Reversements</p>
            </div>
            <div className="flex flex-col gap-0">
              {[
                { label: "En attente", value: stats?.payouts?.pendingCount ?? "…", dot: "bg-orange-400", icon: TbHourglass },
                { label: "À verser",   value: stats?.payouts?.totalPending != null ? `${stats.payouts.totalPending.toLocaleString("fr-FR")} €` : "…", dot: "bg-amber-400", icon: TbCurrencyEuro },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${item.dot}`} />
                    <p className="text-xs text-gray-600">{item.label}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{item.value}</p>
                </div>
              ))}
            </div>
            <button
              onClick={() => setActivePage?.("payouts")}
              className="mt-3 w-full py-2 rounded-xl border border-gray-100 text-xs font-medium text-gray-500 hover:bg-gray-50 transition flex items-center justify-center gap-1"
            >
              Gérer les reversements <TbArrowRight size={12} />
            </button>
          </div>

          {/* Réservations globales */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                <TbCalendarCheck className="text-blue-600" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Réservations</p>
            </div>
            <div className="flex flex-col gap-0">
              {[
                { label: "Ce mois",  value: stats?.reservations?.thisMonth ?? "…", dot: "bg-blue-400" },
                { label: "Total",    value: stats?.reservations?.total      ?? "…", dot: "bg-slate-300" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${item.dot}`} />
                    <p className="text-xs text-gray-600">{item.label}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{item.value}</p>
                </div>
              ))}
            </div>
            <button
              onClick={() => setActivePage?.("reservations")}
              className="mt-3 w-full py-2 rounded-xl border border-gray-100 text-xs font-medium text-gray-500 hover:bg-gray-50 transition flex items-center justify-center gap-1"
            >
              Voir toutes les réservations <TbArrowRight size={12} />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}